// PaymentReceipt.js
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa6";
import bgApp from "../assets/bg-app.png";
import logo from "../assets/logo.png";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

// ========== Helpers ==========
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function withRetry(requestFn, maxAttempts = 3, baseDelay = 2000) {
  let lastErr;
  for (let i = 1; i <= maxAttempts; i++) {
    try {
      return await requestFn();
    } catch (err) {
      lastErr = err;
      if (i === maxAttempts) break;
      const jitter = Math.floor(Math.random() * 500);
      await sleep(baseDelay * i + jitter);
    }
  }
  throw lastErr;
}

function toWhatsAppJid(phone) {
  let num = String(phone).replace(/\D/g, "");
  if (num.length === 10) return "521" + num;
  if (num.length === 12 && num.startsWith("52") && num[2] !== "1")
    return "521" + num.slice(2);
  if (num.length === 13 && num.startsWith("521")) return num;
  return num;
}

// ========== Generación de recibo con Canvas nativo (sin html2canvas) ==========
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapText(ctx, text, x, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  let y = 0;
  const lines = [];
  for (const word of words) {
    const test = line + word + " ";
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line.trim());
      line = word + " ";
    } else {
      line = test;
    }
  }
  if (line.trim()) lines.push(line.trim());
  lines.forEach((l, i) => {
    ctx.fillText(l, x, y + i * lineHeight);
  });
  return lines.length * lineHeight;
}

async function generateReceiptCanvas(transaction) {
  const W = 400;
  const pad = 24;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Pre-calcular altura
  canvas.width = W;
  canvas.height = 800; // temporal, se recortará

  let y = pad;

  // Fondo blanco
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, 800);

  // Logo
  try {
    const logoImg = await loadImage(logo);
    const logoW = 80;
    const logoH = (logoImg.height / logoImg.width) * logoW;
    ctx.drawImage(logoImg, (W - logoW) / 2, y, logoW, logoH);
    y += logoH + 12;
  } catch {
    y += 12;
  }

  // Título
  const tipo = transaction.tipo;
  const title = tipo === "adeudo" ? "Adeudo Registrado" : tipo === "abono" ? "Abono Exitoso" : "Pago Exitoso";
  ctx.fillStyle = "#861E3D";
  ctx.font = "bold 22px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(title, W / 2, y);
  y += 30;

  // Línea separadora
  ctx.strokeStyle = "#e0e0e0";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad, y);
  ctx.lineTo(W - pad, y);
  ctx.stroke();
  y += 16;

  // Helper para filas de datos
  ctx.textAlign = "left";
  const addRow = (label, value, opts = {}) => {
    const fontSize = opts.fontSize || 14;
    const color = opts.color || "#333333";
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.fillStyle = color;
    ctx.fillText(label, pad, y);
    const labelW = ctx.measureText(label).width;
    ctx.font = `${fontSize}px Arial, sans-serif`;
    ctx.fillText(` ${value}`, pad + labelW, y);
    y += fontSize + 8;
  };

  // Folio
  if (transaction.folio) {
    addRow("Folio:", transaction.folio);
  }

  // Campos según tipo
  if (tipo === "adeudo") {
    addRow("Cargo del día:", `$${Number(transaction.dayAmount || 0).toFixed(2)}`);
    if (transaction.totalDebt) addRow("Adeudo total:", `$${Number(transaction.totalDebt).toFixed(2)}`);
  } else if (tipo === "abono") {
    addRow("Monto Abonado:", `$${Number(transaction.totalAmount || 0).toFixed(2)}`, { fontSize: 16 });
  } else {
    addRow("Cargo del día:", `$${Number(transaction.dayAmount || 0).toFixed(2)}`);
    if (transaction.extraTotal > 0) addRow("Extras:", `$${Number(transaction.extraTotal).toFixed(2)}`);
    addRow("Total:", `$${Number(transaction.totalAmount || 0).toFixed(2)}`, { fontSize: 16 });
  }

  y += 4;

  // Agente y negocio
  ctx.font = "13px Arial, sans-serif";
  ctx.fillStyle = "#777777";
  const actionLabel = tipo === "adeudo" ? "Registrado por:" : tipo === "abono" ? "Abonado por:" : "Cobrado por:";
  ctx.fillText(`${actionLabel} ${transaction.agentName || ""}`, pad, y);
  y += 18;
  const targetLabel = tipo === "adeudo" ? "Negocio:" : tipo === "abono" ? "A cargo de:" : "Pagado por:";
  ctx.fillText(`${targetLabel} ${transaction.businessName || ""}`, pad, y);
  y += 18;

  // Fecha
  ctx.font = "11px Arial, sans-serif";
  ctx.fillStyle = "#aaaaaa";
  const dateVal = transaction.date?.seconds ? new Date(transaction.date.seconds * 1000) : new Date(transaction.date);
  ctx.fillText(dateVal.toLocaleString("es-MX"), pad, y);
  y += 20;

  // Texto legal
  ctx.font = "9px Arial, sans-serif";
  ctx.fillStyle = "#999999";
  ctx.textAlign = "center";
  const legalText = "Apartado B. Por el uso de la vía pública por comerciantes ambulantes o con puestos fijos y semifijos. Artículo 18.- Los derechos por el uso de la vía pública se causarán conforme a lo siguiente: I. Los comerciantes ambulantes, pagarán de $10.00 diarios; II. Los puestos fijos o semifijos pagarán de $10.00 pesos diarios por m².";
  y += wrapText(ctx, legalText, W / 2, W - pad * 2, 12);
  y += 6;

  ctx.font = "9px Arial, sans-serif";
  ctx.fillStyle = "#999999";
  ctx.fillText("*El presente comprobante no representa un permiso", W / 2, y);
  y += pad;

  // Recortar canvas a la altura real
  const finalCanvas = document.createElement("canvas");
  finalCanvas.width = W;
  finalCanvas.height = y;
  const fCtx = finalCanvas.getContext("2d");
  fCtx.fillStyle = "#ffffff";
  fCtx.fillRect(0, 0, W, y);
  fCtx.drawImage(canvas, 0, 0);

  return new Promise((resolve) => finalCanvas.toBlob(resolve, "image/png"));
}

// ========== Componente ==========
const PaymentReceipt = () => {
  const navigate = useNavigate();
  const locationState = useLocation().state || {};

  const dummyTransaction = {
    businessPhone: "8311760335",
    businessName: "Mi Negocio de Prueba",
    receiptUrl: "https://via.placeholder.com/300",
    agentName: "Sergio",
    ownerName: "Dueño Prueba",
    date: new Date().toISOString(),
    dayAmount: 20.0,
    totalDebt: 200.0,
    totalAmount: 50.0,
    tipo: "pago",
  };

  const initialTransaction = locationState.transaction || dummyTransaction;

  const [transaction, setTransaction] = useState(initialTransaction);
  const [status, setStatus] = useState(initialTransaction.receiptUrl ? "ready" : "generating"); // generating | uploading | sending | ready | sent | error
  const [alert, setAlert] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const processedRef = useRef(false);

  const db = getFirestore();
  const storage = getStorage();

  const formatDateMx = (dateValue) => {
    const date = dateValue?.seconds ? new Date(dateValue.seconds * 1000) : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("es-MX");
  };

  const fetchWhatsAppConfig = async () => {
    const configRef = doc(db, "config", "whatsapp");
    const configSnap = await getDoc(configRef);
    if (!configSnap.exists()) return null;
    const data = configSnap.data() || {};
    return {
      apiToken: data.wa_api_token || "",
      phoneId: data.wa_phone_id || "",
      templateName: data.wa_template_name || "",
      templateLang: data.wa_template_lang || "es_MX",
      sendMode: data.wa_send_mode || "qr",
    };
  };

  const sendCloudTemplate = async ({ config, phone, imageUrl, bodyParams }) => {
    const payload = {
      messaging_product: "whatsapp",
      to: phone,
      type: "template",
      template: {
        name: config.templateName,
        language: { code: config.templateLang || "es_MX" },
        components: [
          {
            type: "body",
            parameters: bodyParams.map((value) => ({
              type: "text",
              text: String(value ?? ""),
            })),
          },
        ],
      },
    };

    if (imageUrl) {
      payload.template.components.unshift({
        type: "header",
        parameters: [{ type: "image", image: { link: imageUrl } }],
      });
    }

    const url = `https://graph.facebook.com/v21.0/${config.phoneId}/messages`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${config.apiToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errPayload = await response.json().catch(() => ({}));
      throw new Error(errPayload?.error?.message || "Error al enviar plantilla.");
    }
    return response.json().catch(() => ({}));
  };

  const sendWhatsApp = async (receiptUrl) => {
    const phoneFormatted = toWhatsAppJid(transaction.businessPhone);
    if (!phoneFormatted || phoneFormatted.length < 12) {
      setAlert({ type: "error", message: "Teléfono del negocio inválido." });
      return;
    }
    const bodyParams = [
      transaction.ownerName || transaction.businessName || "",
      transaction.businessName || "",
      formatDateMx(transaction.date),
    ];
    const config = await fetchWhatsAppConfig();
    if (!config || !config.apiToken || !config.phoneId || !config.templateName) {
      setAlert({ type: "error", message: "Falta configurar WhatsApp API." });
      return;
    }
    if (config.sendMode === "qr") {
      setAlert({ type: "error", message: "El modo QR requiere backend." });
      return;
    }
    await withRetry(
      () => sendCloudTemplate({ config, phone: phoneFormatted, imageUrl: receiptUrl, bodyParams }),
      3,
      2000
    );
  };

  // Flujo principal: generar → subir → enviar WhatsApp
  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    // Si ya tiene receiptUrl (viene de historial), enviar directo
    if (initialTransaction.receiptUrl) {
      setStatus("sending");
      sendWhatsApp(initialTransaction.receiptUrl)
        .then(() => {
          setStatus("sent");
          setAlert({ type: "success", message: "Recibo enviado por WhatsApp exitosamente." });
        })
        .catch((err) => {
          console.error("Error WhatsApp:", err);
          setStatus("ready");
          setAlert({ type: "error", message: `Error al enviar: ${err?.message || "intenta de nuevo."}` });
        });
      return;
    }

    // Sin receiptUrl → generar, subir, guardar en Firestore, enviar WA
    (async () => {
      try {
        // 1. Generar imagen
        setStatus("generating");
        const blob = await generateReceiptCanvas(initialTransaction);

        // 2. Subir a Storage
        setStatus("uploading");
        const collectionName = initialTransaction.tipo === "adeudo" ? "adeudos" : "cobros";
        const urlField = initialTransaction.tipo === "adeudo" ? "receiptURL" : "receiptUrl";
        const storagePath = `receipts/${initialTransaction.id || Date.now()}.png`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, blob, { contentType: "image/png" });
        const receiptUrl = await getDownloadURL(storageRef);

        // 3. Actualizar Firestore
        if (initialTransaction.id) {
          await updateDoc(doc(db, collectionName, initialTransaction.id), { [urlField]: receiptUrl });
        }

        // 4. Mostrar imagen
        setTransaction((prev) => ({ ...prev, receiptUrl }));
        setStatus("sending");

        // 5. Enviar WhatsApp
        await sendWhatsApp(receiptUrl);
        setStatus("sent");
        setAlert({ type: "success", message: "Recibo enviado por WhatsApp exitosamente." });
      } catch (err) {
        console.error("Error en flujo de recibo:", err);
        setStatus("error");
        setAlert({ type: "error", message: `Error: ${err?.message || "intenta reenviar manualmente."}` });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    if (!transaction.receiptUrl) return;
    setIsSending(true);
    setAlert({ type: "info", message: "Enviando por WhatsApp..." });
    try {
      await sendWhatsApp(transaction.receiptUrl);
      setAlert({ type: "success", message: "Recibo enviado por WhatsApp exitosamente." });
    } catch (err) {
      console.error("Error reenvío WhatsApp:", err);
      setAlert({ type: "error", message: `Error al enviar: ${err?.message || "intenta de nuevo."}` });
    } finally {
      setIsSending(false);
    }
  };

  const statusLabel = {
    generating: "Generando comprobante...",
    uploading: "Subiendo comprobante...",
    sending: "Enviando por WhatsApp...",
    ready: "Comprobante listo",
    sent: "Enviado exitosamente",
    error: "Error al procesar",
  };

  const isProcessing = ["generating", "uploading", "sending"].includes(status);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: `url(${bgApp})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>

      {/* Header */}
      <header className="sticky top-0 z-40 w-full px-4 py-3 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, rgba(112,23,48,0.95), rgba(134,30,61,0.9))", backdropFilter: "blur(10px)" }}>
        <button
          onClick={() => navigate("/cobrador")}
          className="w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-all active:scale-95"
        >
          <FaArrowLeft size={16} />
        </button>
        <img src={logo} alt="Logo" className="h-9 object-contain" />
        <div className="w-10" />
      </header>

      {/* Content */}
      <div className="flex-1 w-full px-4 pt-6 pb-24 flex flex-col items-center" style={{ background: "linear-gradient(180deg, rgba(92,3,30,0.8) 0%, rgba(60,2,20,0.9) 100%)" }}>

        {/* Title badge */}
        <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5 ${
          transaction.tipo === "adeudo"
            ? "bg-red-500/20 border border-red-400/30 text-red-200"
            : transaction.tipo === "abono"
            ? "bg-blue-500/20 border border-blue-400/30 text-blue-200"
            : "bg-emerald-500/20 border border-emerald-400/30 text-emerald-200"
        }`}>
          {transaction.tipo === "adeudo"
            ? "Notificación de Adeudo"
            : transaction.tipo === "abono"
            ? "Notificación de Abono"
            : "Recibo de Pago"}
        </span>

        {/* Receipt image o estado de generación */}
        {transaction.receiptUrl ? (
          <div className="relative overflow-hidden rounded-2xl shadow-2xl mb-6 max-w-sm w-full"
            style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
            <img
              src={transaction.receiptUrl}
              alt="Comprobante"
              className="w-full h-auto"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 mb-6 py-10 px-6 rounded-2xl max-w-sm w-full bg-white/5 border border-white/10">
            <svg className="animate-spin h-8 w-8 text-white/50" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-white/70 text-sm font-medium">{statusLabel[status]}</p>
          </div>
        )}

        {/* Status indicator */}
        {isProcessing && transaction.receiptUrl && (
          <div className="flex items-center gap-2 mb-4">
            <svg className="animate-spin h-4 w-4 text-white/50" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-white/50 text-sm">{statusLabel[status]}</p>
          </div>
        )}

        {/* WhatsApp send button */}
        <button
          onClick={handleResend}
          disabled={isSending || isProcessing || !transaction.receiptUrl}
          className={`flex items-center justify-center gap-2 w-full max-w-sm py-3.5 rounded-2xl font-bold text-sm shadow-lg transition-all ${
            isSending || isProcessing || !transaction.receiptUrl
              ? "bg-white/10 text-white/40 cursor-not-allowed"
              : "bg-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98]"
          }`}
        >
          <FaWhatsapp size={18} />
          {isSending || status === "sending" ? "Enviando..." : isProcessing ? statusLabel[status] : "Reenviar por WhatsApp"}
        </button>

        {/* Status alert */}
        {alert && (
          <div className={`mt-4 px-4 py-3 rounded-xl text-sm font-medium text-center max-w-sm w-full ${
            alert.type === "success"
              ? "bg-emerald-500/20 border border-emerald-400/30 text-emerald-200"
              : alert.type === "info"
              ? "bg-blue-500/20 border border-blue-400/30 text-blue-200"
              : "bg-red-500/20 border border-red-400/30 text-red-200"
          }`}>
            {alert.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReceipt;
